/**
 * Timeline V2 Design System Tokens
 *
 * Centralized design tokens for timeline styling.
 * Based on Figma design: node-id=1844-20147
 *
 * These tokens ensure:
 * - Consistent styling across all event renderers
 * - Easy theming and design system evolution
 * - Single source of truth for visual properties
 */

export const TIMELINE_DESIGN_TOKENS = {
  /**
   * Typography scales
   */
  typography: {
    /** Main event text (14px) */
    eventText: 'text-sm',
    /** Timestamps (12px) */
    timestamp: 'text-xs',
    /** Sub-item text (14px) */
    subItem: 'text-sm',
    /** Font weight for actors and field names */
    emphasis: 'font-semibold',
  },

  /**
   * Color palette
   */
  colors: {
    /** Main event text */
    eventText: 'text-base-content',
    /** Timestamp color (#414651) */
    timestamp: '#414651',
    /** Sub-item text */
    subItemText: 'text-base-content',
    /** Timeline line */
    timelineLine: 'bg-black/[0.33]',
    /** Icon background */
    iconBg: 'bg-white',
    /** Icon color */
    iconColor: 'text-base-content',
  },

  /**
   * Spacing and sizing
   */
  spacing: {
    /** Main event icon size (26px circle) */
    iconSize: {
      main: 'size-[26px]',
      /** Sub-item icon size (18px) */
      sub: 'size-[18px]',
    },
    /** Vertical timeline line */
    verticalLine: {
      /** Main timeline line (1px width) */
      main: 'w-px',
      /** Sub-item connecting line height (14px) */
      subHeight: 'h-[14px]',
    },
    /** Gap between icon and content */
    iconGap: 'gap-3',
    /** Indentation for sub-items */
    subItemIndent: '',
    /** Vertical spacing between events */
    eventSpacing: 'space-y-4',
  },

  /**
   * Layout properties
   */
  layout: {
    /** Event container */
    eventContainer: 'flex items-start gap-3',
    /** Content area (between icon and timestamp) */
    contentArea: 'flex-1 flex items-start justify-between',
    /** Sub-items container */
    subItemsContainer: 'relative',
  },

  /**
   * Interactive states
   */
  states: {
    /** Link hover color */
    linkHover: 'hover:underline',
    /** Focus ring */
    focusRing: 'focus:outline-none focus:ring-2 focus:ring-primary',
  },
} as const;

/**
 * Helper type for accessing token values
 */
export type TimelineDesignTokens = typeof TIMELINE_DESIGN_TOKENS;

/**
 * Helper to get timestamp color as CSS variable or hex
 */
export function getTimestampColor(): string {
  return TIMELINE_DESIGN_TOKENS.colors.timestamp;
}
